import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { mainImg } from "../lib/mainImages";
import { pageUrl } from "../lib/routes";

const contactPrimary = mainImg("J_S_TV_installation_services_IV.jpg");
const contactSecondary = mainImg("J_S_system_configuration.jpg");

type Status = "idle" | "submitting" | "success" | "error";

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

const Contact: React.FC = () => {
  const [status, setStatus] = useState<Status>("idle");
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", service: "", heardAbout: "", message: "", website: "" });

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("submitting");
    try {
      const response = await fetch(`${API_BASE}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!response.ok) {
        setStatus("error");
        return;
      }
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    url: pageUrl("/contact"),
    mainEntity: {
      "@type": "Organization",
      "@id": "https://jandsav.com/#organization",
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+1-508-450-0490",
        contactType: "customer service",
        areaServed: ["US-MA", "US-CT"],
        availableLanguage: ["English"],
      },
    },
  };

  const contactPointSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPoint",
    telephone: "+1-508-450-0490",
    contactType: "customer service",
    areaServed: ["US-MA", "US-CT"],
    availableLanguage: ["English"],
  };

  return (
    <>
      <Helmet>
        <title>J&amp;S Audio Video Solutions – Contact – Central MA</title>
        <meta
          name="description"
          content="Contact J&S Audio Video Solutions for installation services, AV equipment supply, and technical support across Central Massachusetts. Call 508-450-0490."
        />
        <meta property="og:title" content="Contact J&S Audio Video Solutions" />
        <script type="application/ld+json">{JSON.stringify(contactSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(contactPointSchema)}</script>
      </Helmet>

      <section className="subhero" data-testid="subhero-contact">
        <div className="container">
          <span className="eyebrow" style={{ color: "var(--color-accent)" }}>Get in touch</span>
          <h1>Tell us about the room.</h1>
          <p className="lead">
            Estimates are free. Send us a few project details below and a member of the team will get back to you within
            one business day. Prefer to talk? Call <a href="tel:+15084500490" style={{ color: "inherit", fontWeight: 700 }}>508-450-0490</a>.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container split" style={{ alignItems: "start" }}>
          <div>
            <h2>Send us the details</h2>
            <p style={{ marginTop: "1rem", marginBottom: "var(--space-5)" }}>
              The more you can tell us about the room, the faster we can come back with useful next steps.
            </p>
            {status === "success" ? (
              <div className="form-success" data-testid="form-success">
                <h3 style={{ marginBottom: "0.5rem" }}>Thanks, message received.</h3>
                <p style={{ color: "#166534" }}>A member of the team will follow up within one business day. If your project is time-sensitive, call <a href="tel:+15084500490" style={{ color: "#166534", fontWeight: 700 }}>508-450-0490</a>.</p>
              </div>
            ) : (
              <form className="form" onSubmit={onSubmit} data-testid="contact-form" noValidate>
                <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
                  <label htmlFor="website">Do not fill this in</label>
                  <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={onChange} />
                </div>
                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="name">Name</label>
                    <input id="name" name="name" type="text" required value={form.name} onChange={onChange} autoComplete="name" data-testid="input-name" />
                  </div>
                  <div className="form-field">
                    <label htmlFor="phone">Phone</label>
                    <input id="phone" name="phone" type="tel" required value={form.phone} onChange={onChange} autoComplete="tel" data-testid="input-phone" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="email">Email</label>
                    <input id="email" name="email" type="email" required value={form.email} onChange={onChange} autoComplete="email" data-testid="input-email" />
                  </div>
                  <div className="form-field">
                    <label htmlFor="city">City / Town</label>
                    <input id="city" name="city" type="text" required value={form.city} onChange={onChange} autoComplete="address-level2" data-testid="input-city" />
                  </div>
                </div>
                <div className="form-field">
                  <label htmlFor="service">Service interest</label>
                  <select id="service" name="service" required value={form.service} onChange={onChange} data-testid="select-service">
                    <option value="">Select a service…</option>
                    <option value="home-theater">Home Theater Systems</option>
                    <option value="sound-systems">Sound Systems</option>
                    <option value="commercial-av">Commercial AV Solutions</option>
                    <option value="other">Other / Technical Support</option>
                  </select>
                </div>
                <div className="form-field">
                  <label htmlFor="heardAbout">How did you hear about us? (optional)</label>
                  <select id="heardAbout" name="heardAbout" value={form.heardAbout} onChange={onChange} data-testid="select-heard-about">
                    <option value="">Select an option…</option>
                    <option value="Google">Google</option>
                    <option value="Bing">Bing</option>
                    <option value="ChatGPT">ChatGPT</option>
                    <option value="Facebook">Facebook</option>
                    <option value="Instagram">Instagram</option>
                    <option value="Referral">Referral (friend or family)</option>
                    <option value="Nextdoor">Nextdoor</option>
                    <option value="Yelp">Yelp</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-field">
                  <label htmlFor="message">Tell us about the project</label>
                  <textarea id="message" name="message" required value={form.message} onChange={onChange} placeholder="Room type, size, what you're trying to accomplish, anything else relevant…" data-testid="input-message" />
                </div>
                {status === "error" && (
                  <p style={{ color: "#b91c1c", marginTop: "0.25rem" }} data-testid="form-error">
                    Something went wrong sending your message. Please call us at <a href="tel:+15084500490" style={{ color: "#b91c1c", fontWeight: 700 }}>508-450-0490</a> or try again.
                  </p>
                )}
                <button type="submit" className="btn" disabled={status === "submitting"} data-testid="button-submit">
                  {status === "submitting" ? "Sending…" : "Send Message"}
                </button>
              </form>
            )}
          </div>

          <div>
            <h2>Prefer to talk?</h2>
            <p style={{ marginTop: "1rem" }}>
              Our hours are Sun to Sat, 9 AM to 6 PM.
            </p>

            <div style={{ marginTop: "var(--space-5)", padding: "var(--space-5)", background: "var(--color-primary)", color: "var(--color-white)", borderRadius: "var(--radius-md)" }}>
              <div style={{ fontSize: "0.85rem", color: "var(--color-accent)", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 600 }}>Phone</div>
              <a href="tel:+15084500490" style={{ display: "inline-block", color: "var(--color-white)", fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: "1.3rem", marginTop: "0.4rem" }} data-testid="link-contact-phone">
                508-450-0490
              </a>
              <div><span style={{ display: "inline-block", color: "var(--color-white)", fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: "1.3rem", marginTop: "0.4rem" }}>31 Crestwood Rd, Leicester, MA 01524</span></div>
              <div style={{ marginTop: "var(--space-3)", fontSize: "0.9rem", color: "rgba(255,255,255,0.7)" }}>
                Sun to Sat, 9:00 AM to 6:00 PM
              </div>
            </div>

            <div style={{ marginTop: "var(--space-5)" }}>
              <h3>Service area</h3>
              <p style={{ marginTop: "0.5rem" }}>
                We cover Central Massachusetts and parts of Metro West.
              </p>
            </div>

            <figure className="contact-figure" data-testid="contact-figure">
              <img
                src={contactPrimary.src}
                alt="Custom built-in TV over a linear fireplace installed by J&S Audio Video Solutions in a Central Massachusetts home"
                width={contactPrimary.width}
                height={contactPrimary.height}
                loading="lazy"
                decoding="async"
              />
            </figure>
            <figure className="contact-figure" data-testid="contact-figure-secondary">
              <img
                src={contactSecondary.src}
                alt="System configuration and rack work by J&S Audio Video Solutions in Central MA"
                width={contactSecondary.width}
                height={contactSecondary.height}
                loading="lazy"
                decoding="async"
              />
            </figure>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
